import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import apiClient from '../config/axios';
import { BookOpen, Search, Loader2 } from 'lucide-react';

function libraryPreviewText(lib) {
  const parts = [];
  if (lib.header) parts.push(lib.header);
  if (lib.body) parts.push(lib.body);
  if (lib.footer) parts.push(lib.footer);
  return parts.join('\n\n');
}

export function mapLibraryToFormInitial(libraryItem) {
  const buttons = (libraryItem.buttons || []).map((b) => ({
    type: b.type,
    text: b.text || '',
    url: typeof b.url === 'string' ? b.url : '',
    phone_number: b.phone_number || ''
  }));

  const headerFormat = (libraryItem.header_type || libraryItem.header_format || '').toUpperCase();
  const hasTextHeader = libraryItem.header && !['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat);
  const headerType = hasTextHeader ? 'TEXT' : headerFormat || 'none';

  return {
    name: '',
    language: libraryItem.language || 'en_US',
    category: libraryItem.category || 'UTILITY',
    headerType,
    headerText: hasTextHeader ? libraryItem.header : '',
    headerHandle: null,
    headerPreviewUrl: null,
    headerFileName: null,
    headerMediaSource: 'new',
    bodyText: libraryItem.body || '',
    bodyExamples: libraryItem.body_params || [],
    footerText: libraryItem.footer || '',
    buttons
  };
}

const MetaTemplateLibraryPanel = ({
  workspaceId,
  canCreate = true,
  onSelectTemplate
}) => {
  const [search, setSearch] = useState('');
  const [topic, setTopic] = useState('');
  const [usecase, setUsecase] = useState('');
  const [language, setLanguage] = useState('en_US');

  const { data, isLoading, isFetching, error } = useQuery(
    ['metaTemplateLibrary', workspaceId, topic, usecase, language],
    async () => {
      const headers = { 'x-workspace-id': workspaceId };
      const params = { language };
      if (topic) params.topic = topic;
      if (usecase) params.usecase = usecase;
      const res = await apiClient.get('/api/whatsapp/templates/library', { headers, params });
      return res.data;
    },
    { enabled: !!workspaceId, keepPreviousData: true }
  );

  const filterMeta = data?.filters || {};

  const filteredLocal = useMemo(() => {
    const templates = data?.templates || [];
    const needle = search.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter((t) => {
      const hay = [t.name, t.header, t.body, t.footer, t.topic, t.usecase]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [data?.templates, search]);

  const handleSelect = (lib) => {
    if (!canCreate) return;
    onSelectTemplate?.(lib);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 p-4 bg-blue-50/60 border border-blue-100 rounded-xl">
        <BookOpen className="h-5 w-5 text-blue-600 shrink-0" />
        <p className="text-sm text-gray-700">
          Pre-built templates from Meta. Click a template to open the editor sidebar and add it to your account.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search library…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          />
        </div>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
        >
          <option value="">All topics</option>
          {(filterMeta.topics || []).map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={usecase}
          onChange={(e) => setUsecase(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white max-w-[220px]"
        >
          <option value="">All use cases</option>
          {(filterMeta.usecases || []).map((u) => (
            <option key={u} value={u}>{u.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white"
        >
          <option value="en_US">English (US)</option>
          <option value="en_GB">English (UK)</option>
          <option value="hi_IN">Hindi</option>
        </select>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error.response?.data?.error || 'Failed to load template library'}
        </p>
      )}

      <div>
          {(isLoading || isFetching) && filteredLocal.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading Meta library…
            </div>
          ) : filteredLocal.length === 0 ? (
            <p className="text-center text-gray-500 py-16 bg-white border border-gray-200 rounded-xl">
              No library templates match your filters.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredLocal.map((lib) => (
                <button
                  key={`${lib.name}-${lib.language}-${lib.id || ''}`}
                  type="button"
                  onClick={() => handleSelect(lib)}
                  disabled={!canCreate}
                  className="text-left border border-gray-200 rounded-xl p-4 bg-white hover:border-blue-400 hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="font-medium text-gray-900 text-sm">{lib.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full shrink-0">
                      {lib.category || 'UTILITY'}
                    </span>
                  </div>
                  {lib.usecase && (
                    <p className="text-xs text-gray-500 mb-2">{lib.usecase.replace(/_/g, ' ')}</p>
                  )}
                  <p className="text-sm text-gray-600 line-clamp-4 whitespace-pre-wrap">
                    {libraryPreviewText(lib)}
                  </p>
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};

export default MetaTemplateLibraryPanel;
